import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { DocumentDuplicateIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { duplicateBudgetPlan } from '../../viewServices/budget_api';
import { toast } from 'react-hot-toast';
import SpinnerLoad from '../../../moduleComponents/SpinnerLoad';

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

function getYearOptions() {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear - 2; y <= currentYear + 2; y++) {
    years.push(String(y));
  }
  return years;
}

function formatSourceDate(dateStr) {
  if (!dateStr) return '—';
  const [y, m] = dateStr.split('-');
  const month = MONTHS.find((mo) => mo.value === m);
  return `${month?.label || m} ${y}`;
}

const ModalBudgetDuplicate = ({ isOpen, onClose, planId, sourceDate, onSuccess }) => {
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(
    String(currentDate.getMonth() + 1).padStart(2, '0')
  );
  const [selectedYear, setSelectedYear] = useState(String(currentDate.getFullYear()));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      const next = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
      setSelectedMonth(String(next.getMonth() + 1).padStart(2, '0'));
      setSelectedYear(String(next.getFullYear()));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setError(null);
    const targetDate = `${selectedYear}-${selectedMonth}-01`;
    if (targetDate === (sourceDate || '').slice(0, 10).replace(/-\d{2}$/, `-01`)) {
      setError('Target month must differ from source month.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await duplicateBudgetPlan(planId, targetDate);
      toast.success(`Budget duplicated to ${formatSourceDate(`${selectedYear}-${selectedMonth}-01`)}!`);
      onSuccess?.(res.data);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to duplicate budget plan.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
        {submitting && (
          <div className="absolute inset-0 bg-white/70 rounded-2xl flex items-center justify-center z-10">
            <SpinnerLoad />
          </div>
        )}

        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <DocumentDuplicateIcon className="w-5 h-5 text-green-800" />
            <h2 className="text-base font-semibold text-gray-800">Duplicate Budget Plan</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-5 text-sm text-gray-500">
          Source: <span className="font-medium text-gray-700">{formatSourceDate(sourceDate)}</span>
        </div>

        <div className="mb-5">
          <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
            Target Month
          </label>
          <div className="flex gap-2">
            <select
              value={selectedMonth}
              onChange={(e) => { setSelectedMonth(e.target.value); setError(null); }}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-700"
            >
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => { setSelectedYear(e.target.value); setError(null); }}
              className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-700"
            >
              {getYearOptions().map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-4 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-green-800 hover:bg-green-900 text-white transition-colors disabled:opacity-50"
          >
            Duplicate
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ModalBudgetDuplicate;
